import { getIsoWeekNumber } from './isoDates.js';
import { getPlanningItemPromoterIds, getPlanningItemStates } from './giPlanningStateUtils.js';

// Build grouped rows by client for weekly or daily views
// timeUnit: 'weeks' | 'dates'
export const processClientGrouping = ({
  filteredPlanning,
  timeUnit,
  campaignsMap,
  expandedClients,
  selectedStates = []
}) => {
  const clientMap = new Map();

  filteredPlanning.forEach(item => {
    const campaign = campaignsMap.get(item.CampaignId);
    if (!campaign) return;

    const clientName = item.Client || campaign.ClientName || '';
    const promoterIds = getPlanningItemPromoterIds(item, selectedStates);
    const states = getPlanningItemStates(item, selectedStates);

    let timeKey;
    if (timeUnit === 'weeks') {
      const date = new Date(item.Date);
      const weekInfo = getIsoWeekNumber(date);
      timeKey = weekInfo.fullWeekString;
    } else {
      timeKey = item.Date;
    }

    if (!clientMap.has(clientName)) {
      clientMap.set(clientName, {
        client: clientName,
        isClientGroup: true,
        isExpanded: expandedClients?.has(clientName) || false,
        campaigns: new Map(),
        [timeUnit]: new Map(),
        [`first${timeUnit === 'weeks' ? 'Week' : 'Date'}`]: null
      });
    }

    const clientData = clientMap.get(clientName);

    // Aggregate at client level
    if (!clientData[timeUnit].has(timeKey)) {
      clientData[timeUnit].set(timeKey, {
        promoters: new Set(),
        states: new Map()
      });
    }

    const timeData = clientData[timeUnit].get(timeKey);
    promoterIds.forEach((promoterId) => timeData.promoters.add(promoterId));
    states.forEach((state) => {
      timeData.states.set(state, (timeData.states.get(state) || 0) + 1);
    });

    // Per-campaign details for expanded rows
    const campaignKey = `${campaign.Id}-${campaign.Name}`;
    if (!clientData.campaigns.has(campaignKey)) {
      clientData.campaigns.set(campaignKey, {
        campaign: campaign.Name,
        campaignId: campaign.Id,
        client: clientName,
        company: item.Company || '',
        companyId: item.CompanyId || null,
        entity: item.Entity || '',
        entityId: item.EntityId || null,
        [timeUnit]: new Map()
      });
    }

    const campaignData = clientData.campaigns.get(campaignKey);
    if (!campaignData[timeUnit].has(timeKey)) {
      campaignData[timeUnit].set(timeKey, {
        promoters: new Set(),
        states: new Map()
      });
    }

    const campaignTimeData = campaignData[timeUnit].get(timeKey);
    promoterIds.forEach((promoterId) => campaignTimeData.promoters.add(promoterId));
    states.forEach((state) => {
      campaignTimeData.states.set(state, (campaignTimeData.states.get(state) || 0) + 1);
    });
  });

  // Build rows preserving client grouping
  const result = [];
  const firstTimeKey = timeUnit === 'weeks' ? 'firstWeek' : 'firstDate';

  const sortedClients = Array.from(clientMap.values())
    .map(clientGroup => {
      const times = Array.from(clientGroup[timeUnit].keys()).sort();
      clientGroup[firstTimeKey] = times[0];
      return clientGroup;
    })
    .sort((a, b) => a[firstTimeKey].localeCompare(b[firstTimeKey]));

  sortedClients.forEach(clientGroup => {
    const campaignsWithData = Array.from(clientGroup.campaigns.values()).filter(campaignData =>
      campaignData[timeUnit].size > 0
    );

    const clientRow = {
      campaign: clientGroup.client,
      client: clientGroup.client,
      isClientGroup: true,
      isExpanded: clientGroup.isExpanded,
      campaignCount: campaignsWithData.length,
      [firstTimeKey]: clientGroup[firstTimeKey],
      data: Object.fromEntries(
        Array.from(clientGroup[timeUnit].entries()).map(([time, timeData]) => [
          time,
          {
            count: timeData.promoters.size,
            states: Object.fromEntries(timeData.states)
          }
        ])
      )
    };

    result.push(clientRow);

    if (clientGroup.isExpanded) {
      const sortedCampaigns = campaignsWithData
        .map(campaignData => {
          const campaignTimes = Array.from(campaignData[timeUnit].keys()).sort();
          return {
            campaign: campaignData.campaign,
            campaignId: campaignData.campaignId,
            client: campaignData.client,
            company: campaignData.company,
            companyId: campaignData.companyId,
            entity: campaignData.entity,
            entityId: campaignData.entityId,
            isSubRow: true,
            [firstTimeKey]: campaignTimes[0],
            data: Object.fromEntries(
              Array.from(campaignData[timeUnit].entries()).map(([time, timeData]) => [
                time,
                {
                  count: timeData.promoters.size,
                  states: Object.fromEntries(timeData.states)
                }
              ])
            )
          };
        })
        .sort((a, b) => a[firstTimeKey].localeCompare(b[firstTimeKey]));

      result.push(...sortedCampaigns);
    }
  });

  return result;
};


